import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, Plus, Trash2, GripVertical, Play, Pause, 
  Shuffle, Repeat, Clock, Calendar, Video, Youtube,
  Save, RefreshCw, ChevronDown, ChevronUp, List, Grid3X3
} from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";
import { CHANNEL_LOGOS } from "../../constants/channelLogos";

// Days of the week for schedule
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export const PlaylistManagerPage = () => {
  const { channelId } = useParams();
  const navigate = useNavigate();
  
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("loop"); // "loop" or "schedule"
  
  // Simple Loop State
  const [playlistItems, setPlaylistItems] = useState([]);
  const [shuffle, setShuffle] = useState(false);
  const [loop, setLoop] = useState(true);
  const [newItemType, setNewItemType] = useState("youtube");
  const [newItemUrl, setNewItemUrl] = useState("");
  const [newItemTitle, setNewItemTitle] = useState("");
  
  // Scheduled Mode State
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleItems, setScheduleItems] = useState([]);
  const [fillGapsWithLoop, setFillGapsWithLoop] = useState(true);
  const [editingSlot, setEditingSlot] = useState(null);
  const [selectedDay, setSelectedDay] = useState(0); // Monday = 0
  
  // Fetch channel data
  useEffect(() => {
    const fetchChannel = async () => {
      try {
        const response = await axios.get(`${API}/channels/${channelId}`);
        const data = response.data;
        setChannel(data);
        setPlaylistItems(data.playlist_items || []);
        setShuffle(data.playlist_shuffle || false);
        setLoop(data.playlist_loop !== false);
        setScheduleEnabled(data.schedule_enabled || false);
        setScheduleItems(data.schedule_items || []);
        setFillGapsWithLoop(data.fill_gaps_with_loop !== false);
        
        // Set active tab based on current mode
        if (data.schedule_enabled) {
          setActiveTab("schedule");
        }
      } catch (error) {
        console.error("Error fetching channel:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchChannel();
  }, [channelId]);
  
  const [importingPlaylist, setImportingPlaylist] = useState(false);

  // Add playlist item (or expand YouTube playlist)
  const addPlaylistItem = async (forceRefresh = false) => {
    if (!newItemUrl.trim()) return;

    // Check if it's a YouTube playlist URL
    const isPlaylist = newItemUrl.includes("list=");
    if (isPlaylist) {
      setImportingPlaylist(true);
      try {
        const refreshParam = forceRefresh ? "&force_refresh=true" : "";
        const res = await axios.post(
          `${API}/channels/${channelId}/playlist/fetch-youtube-playlist?playlist_url=${encodeURIComponent(newItemUrl.trim())}${refreshParam}`,
          {},
          { headers: getAuthHeader() }
        );
        const items = (res.data.items || []).map((item, idx) => ({
          ...item,
          id: `item_${Date.now()}_${idx}`
        }));
        if (items.length > 0) {
          setPlaylistItems(prev => [...prev, ...items]);
          setNewItemUrl("");
          setNewItemTitle("");
          const cacheNote = res.data.from_cache ? " (from cache)" : "";
          const staleNote = res.data.cache_stale ? " [stale - API unavailable]" : "";
          alert(`Added ${items.length} videos from playlist${cacheNote}${staleNote}`);
        } else {
          alert("No videos found in this playlist");
        }
      } catch (error) {
        console.error("Failed to import playlist:", error);
        alert(error.response?.data?.detail || "Failed to import YouTube playlist");
      } finally {
        setImportingPlaylist(false);
      }
      return;
    }
    
    const newItem = {
      id: `item_${Date.now()}`,
      type: newItemType,
      url: newItemUrl.trim(),
      title: newItemTitle.trim() || extractTitleFromUrl(newItemUrl),
      duration: 0
    };
    
    setPlaylistItems([...playlistItems, newItem]);
    setNewItemUrl("");
    setNewItemTitle("");
  };
  
  // Extract title from URL
  const extractTitleFromUrl = (url) => {
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      return "YouTube Video";
    }
    const filename = url.split("/").pop().split("?")[0];
    return filename || "Video";
  };
  
  // Remove playlist item
  const removePlaylistItem = (index) => {
    const newItems = [...playlistItems];
    newItems.splice(index, 1);
    setPlaylistItems(newItems);
  };
  
  // Move playlist item
  const movePlaylistItem = (index, direction) => {
    const newItems = [...playlistItems];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newItems.length) return;
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    setPlaylistItems(newItems);
  };
  
  // Add schedule slot
  const addScheduleSlot = () => {
    const newSlot = {
      id: `slot_${Date.now()}`,
      day: selectedDay,
      start_hour: 12,
      start_minute: 0,
      end_hour: 13,
      end_minute: 0,
      content_type: "playlist_item",
      content_index: 0,
      content_url: "",
      title: "New Time Slot",
      repeat: "weekly" // "daily", "weekly", "once"
    };
    setScheduleItems([...scheduleItems, newSlot]);
    setEditingSlot(newSlot.id);
  };
  
  // Update schedule slot
  const updateScheduleSlot = (slotId, updates) => {
    setScheduleItems(scheduleItems.map(slot => 
      slot.id === slotId ? { ...slot, ...updates } : slot
    ));
  };
  
  // Remove schedule slot
  const removeScheduleSlot = (slotId) => {
    setScheduleItems(scheduleItems.filter(slot => slot.id !== slotId));
    if (editingSlot === slotId) setEditingSlot(null);
  };
  
  // Get slots for a specific day
  const getSlotsForDay = (dayIndex) => {
    return scheduleItems
      .filter(slot => slot.day === dayIndex || slot.repeat === "daily")
      .sort((a, b) => (a.start_hour * 60 + a.start_minute) - (b.start_hour * 60 + b.start_minute));
  };
  
  // Format time
  const formatTime = (hour, minute = 0) => {
    const h = hour % 12 || 12;
    const ampm = hour < 12 ? "AM" : "PM";
    return `${h}:${minute.toString().padStart(2, "0")} ${ampm}`;
  };
  
  const [resolvingDurations, setResolvingDurations] = useState(false);
  
  const resolveDurations = async () => {
    setResolvingDurations(true);
    try {
      const res = await axios.post(
        `${API}/channels/${channelId}/playlist/resolve-durations`,
        {},
        { headers: getAuthHeader() }
      );
      // Re-fetch channel data to get updated durations
      const updated = await axios.get(`${API}/channels/${channelId}`);
      setPlaylistItems(updated.data.playlist_items || []);
      alert(`Updated ${res.data.updated} of ${res.data.total} item durations`);
    } catch (error) {
      console.error("Failed to resolve durations:", error);
      alert(error.response?.data?.detail || "Failed to resolve durations");
    } finally {
      setResolvingDurations(false);
    }
  };

  // Save all settings
  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/channels/${channelId}/branding`, {
        channel_id: channelId,
        ...channel,
        is_24_7_enabled: true,
        playlist_items: playlistItems,
        playlist_shuffle: shuffle,
        playlist_loop: loop,
        schedule_enabled: scheduleEnabled,
        schedule_items: scheduleItems,
        fill_gaps_with_loop: fillGapsWithLoop
      }, { headers: getAuthHeader() });
      
      alert("Playlist settings saved successfully!");
    } catch (error) {
      console.error("Error saving:", error);
      alert("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }
  
  if (!channel) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] p-8">
        <div className="text-center text-zinc-500">Channel not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <div className="bg-black/50 border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/admin")}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="back-to-admin"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <img 
                src={CHANNEL_LOGOS[channelId] || CHANNEL_LOGOS.channel1}
                alt={channel.name}
                className="h-10 w-auto"
              />
              <div>
                <h1 className="font-secondary text-xl font-bold text-white">Playlist Manager</h1>
                <p className="text-zinc-500 text-sm">{channel.name}</p>
              </div>
            </div>
            <Button
              onClick={saveSettings}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-500 text-white font-secondary font-bold text-sm px-6 py-2 h-auto rounded-none"
              data-testid="save-playlist-btn"
            >
              {saving ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Changes</>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Mode Tabs */}
        <div className="flex gap-2 mb-8">
          <button
            onClick={() => { setActiveTab("loop"); setScheduleEnabled(false); }}
            className={`flex items-center gap-2 px-6 py-3 font-secondary font-bold text-sm uppercase tracking-wider transition-colors ${
              activeTab === "loop" 
                ? "bg-purple-600 text-white" 
                : "bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800"
            }`}
            data-testid="tab-simple-loop"
          >
            <Repeat className="w-4 h-4" />
            Simple Loop
          </button>
          <button
            onClick={() => { setActiveTab("schedule"); setScheduleEnabled(true); }}
            className={`flex items-center gap-2 px-6 py-3 font-secondary font-bold text-sm uppercase tracking-wider transition-colors ${
              activeTab === "schedule" 
                ? "bg-purple-600 text-white" 
                : "bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800"
            }`}
            data-testid="tab-scheduled"
          >
            <Calendar className="w-4 h-4" />
            Scheduled
          </button>
        </div>

        {/* Simple Loop Mode */}
        {activeTab === "loop" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Loop Controls */}
            <div className="bg-zinc-900 border border-white/10 p-6">
              <h2 className="font-secondary text-lg font-bold text-white uppercase mb-4">Playback Settings</h2>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={loop}
                    onChange={(e) => setLoop(e.target.checked)}
                    className="w-5 h-5 accent-purple-500"
                  />
                  <Repeat className="w-5 h-5 text-purple-500" />
                  <span className="text-white group-hover:text-purple-400 transition-colors">Loop Playlist</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={shuffle}
                    onChange={(e) => setShuffle(e.target.checked)}
                    className="w-5 h-5 accent-purple-500"
                  />
                  <Shuffle className="w-5 h-5 text-purple-500" />
                  <span className="text-white group-hover:text-purple-400 transition-colors">Shuffle</span>
                </label>
              </div>
            </div>

            {/* Add New Item */}
            <div className="bg-zinc-900 border border-white/10 p-6">
              <h2 className="font-secondary text-lg font-bold text-white uppercase mb-4">Add Content</h2>
              <div className="flex gap-3">
                <select
                  value={newItemType}
                  onChange={(e) => setNewItemType(e.target.value)}
                  className="bg-black border border-white/10 text-white px-4 py-3 text-sm w-40"
                  data-testid="new-item-type"
                >
                  <option value="youtube">YouTube Video</option>
                  <option value="youtube_playlist">YouTube Playlist</option>
                  <option value="mp4">MP4 URL</option>
                </select>
                <input
                  type="text"
                  value={newItemTitle}
                  onChange={(e) => setNewItemTitle(e.target.value)}
                  placeholder="Title (optional)"
                  className="bg-black border border-white/10 text-white px-4 py-3 text-sm w-48"
                  data-testid="new-item-title"
                />
                <input
                  type="url"
                  value={newItemUrl}
                  onChange={(e) => setNewItemUrl(e.target.value)}
                  placeholder="Paste URL here..."
                  className="flex-1 bg-black border border-white/10 text-white px-4 py-3 text-sm font-mono"
                  data-testid="new-item-url"
                  onKeyDown={(e) => e.key === "Enter" && addPlaylistItem()}
                />
                <Button
                  onClick={() => addPlaylistItem(false)}
                  disabled={!newItemUrl.trim() || importingPlaylist}
                  className="bg-purple-600 hover:bg-purple-500 text-white px-6 py-3 h-auto rounded-none"
                  data-testid="add-item-btn"
                >
                  {importingPlaylist ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Plus className="w-5 h-5" />
                  )}
                </Button>
                {newItemUrl.includes("list=") && (
                  <Button
                    onClick={() => addPlaylistItem(true)}
                    disabled={!newItemUrl.trim() || importingPlaylist}
                    className="bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-3 h-auto rounded-none text-xs"
                    data-testid="force-refresh-btn"
                    title="Bypass cache and fetch fresh data from YouTube"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <p className="text-zinc-600 text-xs mt-2">
                Supported: YouTube videos, YouTube playlists (auto-expands, cached 24h — use <span className="text-zinc-400">refresh</span> icon to bypass), direct MP4 URLs
              </p>
            </div>

            {/* Playlist Items */}
            <div className="bg-zinc-900 border border-white/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-secondary text-lg font-bold text-white uppercase">
                  Playlist Items ({playlistItems.length})
                </h2>
                {playlistItems.length > 0 && (
                  <Button
                    onClick={resolveDurations}
                    disabled={resolvingDurations}
                    className="bg-zinc-800 hover:bg-zinc-700 text-white text-xs px-4 py-2 h-auto rounded-none"
                    data-testid="resolve-durations-btn"
                  >
                    {resolvingDurations ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Clock className="w-4 h-4 mr-2" />
                    )}
                    {resolvingDurations ? "Fetching..." : "Fetch Durations"}
                  </Button>
                )}
              </div>
              
              {playlistItems.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-white/10">
                  <List className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500">No items in playlist. Add YouTube videos or MP4 URLs above.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {playlistItems.map((item, index) => (
                    <motion.div
                      key={item.id || index}
                      layout
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="flex items-center gap-3 p-4 bg-black/50 border border-white/5 group hover:border-purple-500/30 transition-colors"
                      data-testid={`playlist-item-${index}`}
                    >
                      {/* Drag Handle / Order */}
                      <div className="flex items-center gap-2 text-zinc-600">
                        <GripVertical className="w-4 h-4 cursor-grab" />
                        <span className="w-6 text-center font-mono text-sm">{index + 1}</span>
                      </div>
                      
                      {/* Type Icon */}
                      <div className={`p-2 ${item.type === "youtube" || item.type === "youtube_playlist" ? "bg-red-500/20" : "bg-blue-500/20"}`}>
                        {item.type === "youtube" || item.type === "youtube_playlist" ? (
                          <Youtube className="w-5 h-5 text-red-500" />
                        ) : (
                          <Video className="w-5 h-5 text-blue-500" />
                        )}
                      </div>
                      
                      {/* Title & URL & Duration */}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{item.title || "Untitled"}</p>
                        <div className="flex items-center gap-3">
                          <p className="text-zinc-600 text-xs truncate font-mono">{item.url}</p>
                          {item.duration > 0 && (
                            <span className="text-zinc-500 text-xs font-mono shrink-0 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {Math.floor(item.duration / 3600) > 0 && `${Math.floor(item.duration / 3600)}h `}
                              {Math.floor((item.duration % 3600) / 60)}m
                              {item.duration % 60 > 0 && ` ${item.duration % 60}s`}
                            </span>
                          )}
                          {(!item.duration || item.duration === 0) && (
                            <span className="text-yellow-500/60 text-[10px] shrink-0">No duration</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => movePlaylistItem(index, -1)}
                          disabled={index === 0}
                          className="p-2 text-zinc-500 hover:text-white disabled:opacity-30"
                          title="Move Up"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => movePlaylistItem(index, 1)}
                          disabled={index === playlistItems.length - 1}
                          className="p-2 text-zinc-500 hover:text-white disabled:opacity-30"
                          title="Move Down"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removePlaylistItem(index)}
                          className="p-2 text-red-500 hover:text-red-400"
                          title="Remove"
                          data-testid={`remove-item-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Scheduled Mode */}
        {activeTab === "schedule" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Schedule Settings */}
            <div className="bg-zinc-900 border border-white/10 p-6">
              <h2 className="font-secondary text-lg font-bold text-white uppercase mb-4">Schedule Settings</h2>
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={fillGapsWithLoop}
                  onChange={(e) => setFillGapsWithLoop(e.target.checked)}
                  className="w-5 h-5 accent-purple-500"
                />
                <span className="text-white group-hover:text-purple-400 transition-colors">
                  Fill unscheduled time with Simple Loop playlist
                </span>
              </label>
              <p className="text-zinc-600 text-xs mt-2 ml-8">
                When enabled, the Simple Loop playlist will play during any time not covered by scheduled slots.
              </p>
            </div>

            {/* Day Selector */}
            <div className="bg-zinc-900 border border-white/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-secondary text-lg font-bold text-white uppercase">Weekly Schedule</h2>
                <Button
                  onClick={addScheduleSlot}
                  className="bg-purple-600 hover:bg-purple-500 text-white text-sm px-4 py-2 h-auto rounded-none"
                  data-testid="add-schedule-slot"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Time Slot
                </Button>
              </div>
              
              {/* Day Tabs */}
              <div className="flex gap-1 mb-6 overflow-x-auto pb-2">
                {DAYS.map((day, index) => {
                  const slotsCount = getSlotsForDay(index).length;
                  return (
                    <button
                      key={day}
                      onClick={() => setSelectedDay(index)}
                      className={`px-4 py-2 text-sm font-bold uppercase whitespace-nowrap transition-colors ${
                        selectedDay === index
                          ? "bg-purple-600 text-white"
                          : "bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700"
                      }`}
                      data-testid={`day-tab-${index}`}
                    >
                      {day.slice(0, 3)}
                      {slotsCount > 0 && (
                        <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">{slotsCount}</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Time Slots for Selected Day */}
              <div className="space-y-3">
                {getSlotsForDay(selectedDay).length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-white/10">
                    <Clock className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                    <p className="text-zinc-500">No scheduled content for {DAYS[selectedDay]}.</p>
                    <p className="text-zinc-600 text-sm mt-1">Click "Add Time Slot" to schedule content.</p>
                  </div>
                ) : (
                  getSlotsForDay(selectedDay).map((slot) => (
                    <div
                      key={slot.id}
                      className={`border transition-colors ${
                        editingSlot === slot.id 
                          ? "border-purple-500 bg-purple-500/5" 
                          : "border-white/10 bg-black/30"
                      }`}
                    >
                      {/* Slot Header */}
                      <div 
                        className="flex items-center justify-between p-4 cursor-pointer"
                        onClick={() => setEditingSlot(editingSlot === slot.id ? null : slot.id)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-purple-500 font-mono font-bold">
                            {formatTime(slot.start_hour, slot.start_minute)} - {formatTime(slot.end_hour, slot.end_minute)}
                          </div>
                          <div className="text-white">{slot.title}</div>
                          {slot.repeat === "daily" && (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs uppercase">Daily</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); removeScheduleSlot(slot.id); }}
                            className="p-2 text-red-500 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          {editingSlot === slot.id ? (
                            <ChevronUp className="w-5 h-5 text-zinc-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-zinc-500" />
                          )}
                        </div>
                      </div>
                      
                      {/* Slot Editor */}
                      <AnimatePresence>
                        {editingSlot === slot.id && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="p-4 pt-0 border-t border-white/10 space-y-4">
                              {/* Title */}
                              <div>
                                <label className="block text-xs text-zinc-500 uppercase mb-1">Title</label>
                                <input
                                  type="text"
                                  value={slot.title}
                                  onChange={(e) => updateScheduleSlot(slot.id, { title: e.target.value })}
                                  className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm"
                                />
                              </div>
                              
                              {/* Time Range */}
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs text-zinc-500 uppercase mb-1">Start Time</label>
                                  <div className="flex gap-2">
                                    <select
                                      value={slot.start_hour}
                                      onChange={(e) => updateScheduleSlot(slot.id, { start_hour: parseInt(e.target.value) })}
                                      className="flex-1 bg-black border border-white/10 text-white px-3 py-2 text-sm"
                                    >
                                      {HOURS.map(h => (
                                        <option key={h} value={h}>{formatTime(h)}</option>
                                      ))}
                                    </select>
                                    <select
                                      value={slot.start_minute}
                                      onChange={(e) => updateScheduleSlot(slot.id, { start_minute: parseInt(e.target.value) })}
                                      className="w-20 bg-black border border-white/10 text-white px-3 py-2 text-sm"
                                    >
                                      <option value={0}>:00</option>
                                      <option value={15}>:15</option>
                                      <option value={30}>:30</option>
                                      <option value={45}>:45</option>
                                    </select>
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs text-zinc-500 uppercase mb-1">End Time</label>
                                  <div className="flex gap-2">
                                    <select
                                      value={slot.end_hour}
                                      onChange={(e) => updateScheduleSlot(slot.id, { end_hour: parseInt(e.target.value) })}
                                      className="flex-1 bg-black border border-white/10 text-white px-3 py-2 text-sm"
                                    >
                                      {HOURS.map(h => (
                                        <option key={h} value={h}>{formatTime(h)}</option>
                                      ))}
                                    </select>
                                    <select
                                      value={slot.end_minute}
                                      onChange={(e) => updateScheduleSlot(slot.id, { end_minute: parseInt(e.target.value) })}
                                      className="w-20 bg-black border border-white/10 text-white px-3 py-2 text-sm"
                                    >
                                      <option value={0}>:00</option>
                                      <option value={15}>:15</option>
                                      <option value={30}>:30</option>
                                      <option value={45}>:45</option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Content Source */}
                              <div>
                                <label className="block text-xs text-zinc-500 uppercase mb-1">Content</label>
                                <select
                                  value={slot.content_type}
                                  onChange={(e) => updateScheduleSlot(slot.id, { content_type: e.target.value })}
                                  className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm mb-2"
                                >
                                  <option value="playlist_item">From Loop Playlist</option>
                                  <option value="custom_url">Custom URL</option>
                                </select>
                                
                                {slot.content_type === "playlist_item" && playlistItems.length > 0 ? (
                                  <select
                                    value={slot.content_index}
                                    onChange={(e) => updateScheduleSlot(slot.id, { content_index: parseInt(e.target.value) })}
                                    className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm"
                                  >
                                    {playlistItems.map((item, idx) => (
                                      <option key={idx} value={idx}>
                                        {idx + 1}. {item.title || item.url}
                                      </option>
                                    ))}
                                  </select>
                                ) : slot.content_type === "custom_url" ? (
                                  <input
                                    type="url"
                                    value={slot.content_url || ""}
                                    onChange={(e) => updateScheduleSlot(slot.id, { content_url: e.target.value })}
                                    placeholder="https://youtube.com/watch?v=..."
                                    className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm font-mono"
                                  />
                                ) : (
                                  <p className="text-zinc-600 text-sm">Add items to the Simple Loop playlist first.</p>
                                )}
                              </div>
                              
                              {/* Repeat */}
                              <div>
                                <label className="block text-xs text-zinc-500 uppercase mb-1">Repeat</label>
                                <select
                                  value={slot.repeat}
                                  onChange={(e) => updateScheduleSlot(slot.id, { repeat: e.target.value })}
                                  className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm"
                                >
                                  <option value="weekly">Weekly (every {DAYS[slot.day]})</option>
                                  <option value="daily">Daily (every day)</option>
                                  <option value="once">Once only</option>
                                </select>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Visual Timeline Preview */}
            <div className="bg-zinc-900 border border-white/10 p-6">
              <h2 className="font-secondary text-lg font-bold text-white uppercase mb-4">
                {DAYS[selectedDay]} Timeline
              </h2>
              <div className="relative h-16 bg-black/50 border border-white/10 overflow-hidden">
                {/* Hour markers */}
                <div className="absolute inset-0 flex">
                  {HOURS.map(hour => (
                    <div 
                      key={hour} 
                      className="flex-1 border-r border-white/5 relative"
                    >
                      {hour % 3 === 0 && (
                        <span className="absolute -bottom-5 left-0 text-[10px] text-zinc-600">
                          {formatTime(hour).split(" ")[0]}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Scheduled blocks */}
                {getSlotsForDay(selectedDay).map(slot => {
                  const startPercent = ((slot.start_hour * 60 + slot.start_minute) / (24 * 60)) * 100;
                  const endPercent = ((slot.end_hour * 60 + slot.end_minute) / (24 * 60)) * 100;
                  const width = endPercent - startPercent;
                  
                  return (
                    <div
                      key={slot.id}
                      className="absolute top-1 bottom-1 bg-purple-500/50 border border-purple-500 cursor-pointer hover:bg-purple-500/70 transition-colors"
                      style={{ left: `${startPercent}%`, width: `${width}%` }}
                      onClick={() => setEditingSlot(slot.id)}
                      title={`${slot.title}: ${formatTime(slot.start_hour, slot.start_minute)} - ${formatTime(slot.end_hour, slot.end_minute)}`}
                    >
                      <span className="text-[10px] text-white px-1 truncate block">
                        {slot.title}
                      </span>
                    </div>
                  );
                })}
                
                {/* Fill gaps indicator */}
                {fillGapsWithLoop && (
                  <div className="absolute inset-0 bg-zinc-800/30 -z-10" />
                )}
              </div>
              <div className="flex items-center gap-4 mt-6 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-purple-500/50 border border-purple-500" />
                  <span className="text-zinc-400">Scheduled Content</span>
                </div>
                {fillGapsWithLoop && (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-zinc-800/50 border border-zinc-700" />
                    <span className="text-zinc-400">Loop Playlist (fills gaps)</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PlaylistManagerPage;
